<template>
  <Teleport to="body">
    <transition name="modal-fade">
      <div
        v-if="showModal"
        class="ai-modal-overlay"
        :class="{ 'ai-dragging-active': !isInitialPosition }"
      >
        <div
          ref="modalRef"
          class="ai-modal"
          :class="{ 'is-dragging': isDragging }"
          :style="modalStyle"
          @click.stop
          @mousedown="startDrag"
          @touchstart="startDrag"
        >
          <div class="ai-modal-header" :class="{ draggable: true }">
            <div class="ai-header-content">
              <Icon icon="mdi:sparkles" width="18" height="18" class="ai-icon" />
              <div class="ai-header-text">
                <h4 class="ai-title">AI Personalization</h4>
                <p class="ai-subtitle">
                  Generate personalized content for {{ contactName || "Contact" }}
                </p>
              </div>
            </div>
            <div class="ai-header-actions">
              <div class="drag-handle" title="Drag to move">
                <Icon icon="mdi:drag" width="16" height="16" />
              </div>
              <button type="button" class="btn-close-ai" @click="close">
                <Icon icon="mdi:close" width="18" height="18" />
              </button>
            </div>
          </div>
          <div class="ai-modal-body">
            <!-- Generation Options -->
            <div class="ai-options">
              <button class="ai-option" @click="handleAIAction('call')" :disabled="isGenerating">
                <Icon icon="mdi:phone-outline" width="18" height="18" />
                <span>Generate Call Script</span>
              </button>
            </div>

            <!-- Simple Generating Text -->
            <div v-if="isGenerating" class="ai-generating">
              <p class="generating-text">Generating...</p>
              <div class="personalizing-box">
                <p class="personalizing-text">Personalizing content...</p>
              </div>
            </div>

            <!-- Generated Content -->
            <div v-if="showGeneratedContent" class="ai-generated">
              <div class="generated-header">
                <div class="generated-title-row">
                  <h3 class="generated-title">Generated Content</h3>
                  <span v-if="selectedType" class="channel-pill">{{ channelLabel }}</span>
                </div>
                <button class="copy-btn" @click="copyContent">
                  <Icon icon="mdi:content-copy" width="16" height="16" />
                  <span>Copy</span>
                </button>
              </div>
              <div class="generated-content">
                <pre class="content-text">{{ generatedContent }}</pre>
              </div>
            </div>
          </div>
        </div>
      </div>
    </transition>
  </Teleport>
</template>

<script lang="ts">
import { defineComponent, ref, computed, watch } from "vue";
import { Icon } from "@iconify/vue";
import AIGenerationService from "@/services/ai/ai-generation";

export type AIPersonalizationChannel = "call" | "email" | "linkedin" | "whatsapp" | "sms";

export default defineComponent({
  name: "AIPersonalizationModal",
  components: { Icon },
  props: {
    showModal: {
      type: Boolean,
      required: true,
    },
    contactName: {
      type: String,
      default: "",
    },
    icpName: {
      type: String,
      default: "",
    },
    defaultType: {
      type: String as () => AIPersonalizationChannel | "",
      default: "",
    },
    contactId: {
      type: String,
      default: "",
    },
    icpId: {
      type: String,
      default: "",
    },
  },
  emits: ["close", "generate", "generated"],
  setup(props, { emit }) {
    const isGenerating = ref(false);
    const generatedContent = ref("");
    const selectedType = ref("");
    const showGeneratedContent = ref(false);

    const isDragging = ref(false);
    const dragStart = ref({ x: 0, y: 0 });
    const modalPosition = ref({ x: 0, y: 0 });
    const isInitialPosition = ref(true);
    const modalRef = ref<HTMLElement | null>(null);

    const modalStyle = computed(() => {
      if (isInitialPosition.value) {
        return {
          position: "fixed" as const,
          left: "auto",
          right: "2%",
          top: "8%",
          transform: "none",
          width: "85%",
          maxWidth: "420px",
          minHeight: "480px",
          maxHeight: "88vh",
          borderRadius: "12px",
          zIndex: "10060",
        };
      }
      return {
        position: "fixed" as const,
        left: `${modalPosition.value.x}px`,
        top: `${modalPosition.value.y}px`,
        right: "auto",
        transform: "none",
        width: "85%",
        maxWidth: "420px",
        minHeight: "480px",
        maxHeight: "88vh",
        borderRadius: "12px",
        zIndex: "10060",
      };
    });

    const startDrag = (event: MouseEvent | TouchEvent) => {
      const target = event.target as HTMLElement;
      const excluded = [
        ".btn-close-ai",
        ".ai-modal-body",
        ".ai-option",
        ".copy-btn",
        ".ai-generated",
      ];
      if (excluded.some((sel) => target.closest(sel))) return;

      event.preventDefault();
      event.stopPropagation();
      isDragging.value = true;

      const clientX = "touches" in event ? event.touches[0].clientX : (event as MouseEvent).clientX;
      const clientY = "touches" in event ? event.touches[0].clientY : (event as MouseEvent).clientY;

      if (isInitialPosition.value) {
        const el = modalRef.value;
        if (el) {
          const rect = el.getBoundingClientRect();
          modalPosition.value = { x: rect.left, y: rect.top };
        } else {
          const maxW = Math.min(420, window.innerWidth * 0.85);
          modalPosition.value = {
            x: window.innerWidth - maxW - window.innerWidth * 0.02,
            y: window.innerHeight * 0.08,
          };
        }
        isInitialPosition.value = false;
      }

      dragStart.value = {
        x: clientX - modalPosition.value.x,
        y: clientY - modalPosition.value.y,
      };

      const addListener = (ev: string, fn: EventListener, opts?: AddEventListenerOptions) =>
        document.addEventListener(ev, fn, opts);
      addListener("mousemove", handleDrag as EventListener, { passive: false });
      addListener("mouseup", stopDrag as EventListener);
      addListener("touchmove", handleDrag as EventListener, { passive: false });
      addListener("touchend", stopDrag as EventListener);
    };

    const handleDrag = (event: MouseEvent | TouchEvent) => {
      if (!isDragging.value) return;
      event.preventDefault();
      event.stopPropagation();
      const clientX = "touches" in event ? event.touches[0].clientX : (event as MouseEvent).clientX;
      const clientY = "touches" in event ? event.touches[0].clientY : (event as MouseEvent).clientY;
      let newX = clientX - dragStart.value.x;
      let newY = clientY - dragStart.value.y;
      const el = modalRef.value;
      if (el) {
        const rect = el.getBoundingClientRect();
        const pad = 10;
        newX = Math.max(pad, Math.min(newX, window.innerWidth - rect.width - pad));
        newY = Math.max(pad, Math.min(newY, window.innerHeight - rect.height - pad));
      }
      modalPosition.value = { x: newX, y: newY };
    };

    const stopDrag = () => {
      isDragging.value = false;
      document.removeEventListener("mousemove", handleDrag as EventListener);
      document.removeEventListener("mouseup", stopDrag as EventListener);
      document.removeEventListener("touchmove", handleDrag as EventListener);
      document.removeEventListener("touchend", stopDrag as EventListener);
    };

    const close = () => {
      isInitialPosition.value = true;
      modalPosition.value = { x: 0, y: 0 };
      emit("close");
    };

    const channelLabels: Record<string, string> = {
      call: "Call Script",
      email: "Email",
      linkedin: "LinkedIn",
      whatsapp: "WhatsApp",
      sms: "SMS",
    };
    const channelLabel = computed(() =>
      selectedType.value ? channelLabels[selectedType.value] || selectedType.value : ""
    );

    const displayName = computed(() => props.contactName || "Contact");

    const getMockContent = (type: string) => {
      const name = displayName.value;
      const contentMap: Record<string, string> = {
        call: `Hi ${name},

I hope this call finds you well. I wanted to reach out personally because I noticed your company has been growing rapidly in the past quarter.

I'd love to discuss how our solution could help streamline your operations and support this growth trajectory. Based on your industry and company size, I believe we could deliver significant value.

Would you have 15 minutes this week to explore this further?`,
        email: `Subject: Quick question about your growth trajectory

Hi ${name},

I hope this email finds you well. I've been following your company's impressive growth and wanted to reach out personally.

I believe our solution could help streamline your operations during this exciting expansion phase. Based on your industry and current challenges, I'm confident we could deliver significant value.

Would you be open to a brief 15-minute conversation this week to explore this further?

Best regards`,
        linkedin: `Hi ${name},

I hope you're doing well! I've been following your company's impressive growth trajectory and wanted to connect.

I believe our solution could be particularly valuable for companies in your industry during expansion phases. I'd love to share some insights that might be relevant to your current challenges.

Would you be interested in a brief conversation to explore potential synergies?

Best regards`,
        whatsapp: `Hi ${name}! I noticed your company's growth and wanted to reach out. I think our solution could help streamline operations—would you have 15 min this week for a quick chat?`,
        sms: `Hi ${name}, quick note: I'd love to share how we're helping companies like yours streamline operations. Got 15 min this week for a call?`,
      };
      return contentMap[type] || "Generated content will appear here...";
    };

    const handleAIAction = async (type: string) => {
      selectedType.value = type;
      isGenerating.value = true;
      showGeneratedContent.value = false;

      if (props.contactId && props.icpId) {
        try {
          const response = await AIGenerationService.generateContent({
            contactId: props.contactId,
            icpId: props.icpId,
            channel: type,
          });

          const result = response.data?.data;
          if (!result?.body) {
            throw new Error("No content returned from AI generation");
          }

          isGenerating.value = false;
          showGeneratedContent.value = true;
          generatedContent.value = result.body;
          emit("generated", { type, content: generatedContent.value });
        } catch (error) {
          console.error("Failed to generate content:", error);
          // Fallback to mock content on error
          isGenerating.value = false;
          showGeneratedContent.value = true;
          generatedContent.value = getMockContent(type);
          emit("generated", { type, content: generatedContent.value });
        }
      } else {
        // Fallback to mock if IDs not provided
        setTimeout(() => {
          isGenerating.value = false;
          showGeneratedContent.value = true;
          generatedContent.value = getMockContent(type);
          emit("generated", { type, content: generatedContent.value });
        }, 3000);
      }
    };

    watch(
      () => props.showModal,
      (visible) => {
        if (visible && props.defaultType) {
          handleAIAction(props.defaultType);
        }
      }
    );

    const copyContent = () => {
      navigator.clipboard.writeText(generatedContent.value);
    };

    const resetGeneration = () => {
      isGenerating.value = false;
      showGeneratedContent.value = false;
      generatedContent.value = "";
      selectedType.value = "";
    };

    return {
      modalRef,
      modalStyle,
      isDragging,
      isInitialPosition,
      startDrag,
      handleAIAction,
      isGenerating,
      generatedContent,
      selectedType,
      showGeneratedContent,
      channelLabel,
      copyContent,
      resetGeneration,
      close,
    };
  },
});
</script>

<style scoped>
.ai-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: transparent;
  display: flex;
  align-items: flex-start;
  justify-content: flex-end;
  z-index: 10060;
  pointer-events: none;
}

.ai-modal-overlay.ai-dragging-active {
  align-items: flex-start;
  justify-content: flex-start;
}

.ai-modal {
  background: #ffffff;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  display: flex;
  flex-direction: column;
  transition: box-shadow 0.2s ease;
  user-select: none;
  pointer-events: auto;
}

.ai-modal.is-dragging {
  box-shadow: 0 25px 80px rgba(0, 0, 0, 0.4);
  cursor: grabbing;
  transition: none;
}

.ai-modal-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 14px 16px;
  border-bottom: 1px solid #e8e8e8;
  transition: background-color 0.2s ease;
}

.ai-modal-header.draggable {
  cursor: grab;
  user-select: none;
}

.ai-modal-header.draggable:hover {
  background-color: #f8f9fa;
}

.ai-modal-header.draggable:active {
  cursor: grabbing;
  background-color: #e9ecef;
}

.ai-header-content {
  display: flex;
  gap: 10px;
  align-items: flex-start;
  flex: 1;
}

.ai-header-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.drag-handle {
  color: #9ca3af;
  cursor: grab;
  padding: 4px;
  border-radius: 4px;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
}

.drag-handle:hover {
  color: #6b7280;
  background-color: #f3f4f6;
}

.ai-icon {
  color: #667eea;
  margin-top: 2px;
}

.ai-header-text {
  flex: 1;
}

.ai-title {
  font-size: 15px;
  font-weight: 600;
  color: #111827;
  margin: 0 0 4px 0;
}

.ai-subtitle {
  font-size: 12px;
  color: #6b7280;
  margin: 0;
}

.btn-close-ai {
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 2px;
  color: #6b7280;
  display: flex;
  align-items: center;
  justify-content: center;
}

.btn-close-ai:hover {
  color: #374151;
}

.ai-modal-body {
  padding: 16px 18px 20px;
  display: flex;
  flex-direction: column;
  flex: 1;
  overflow-y: auto;
}

.ai-options {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 20px;
}

.ai-option {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;
  background: #ffffff;
  border: 1px solid #e8e8e8;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 13px;
  font-weight: 600;
  color: #111827;
  text-align: left;
  min-height: 52px;
  width: 100%;
}

.ai-option:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.ai-option:hover {
  border-color: #6d28d9;
  background: #f9f7ff;
  box-shadow: 0 2px 8px rgba(109, 40, 217, 0.15);
}

.ai-option:active {
  transform: none;
}

.ai-option svg {
  color: #6d28d9;
  flex-shrink: 0;
  width: 18px;
  height: 18px;
}

.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.3s ease;
}

.modal-fade-enter-from,
.modal-fade-leave-to {
  opacity: 0;
}

.generating-text {
  font-size: 12px;
  font-weight: 400;
  color: #9ca3af;
  margin: 0 0 10px 0;
  text-align: left;
}

.personalizing-box {
  background: #ffffff;
  border: 1px solid #e8e8e8;
  border-radius: 8px;
  padding: 14px 16px;
  margin-top: 10px;
  position: relative;
  overflow: hidden;
  min-height: 48px;
  display: flex;
  align-items: center;
}

.personalizing-text {
  font-size: 13px;
  color: #6b7280;
  margin: 0;
  position: relative;
  z-index: 1;
}

.personalizing-box::before {
  content: "";
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(109, 40, 217, 0.1), transparent);
  animation: shimmer 2s infinite;
}

@keyframes shimmer {
  0% {
    left: -100%;
  }
  100% {
    left: 100%;
  }
}

.ai-generated {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.generated-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-bottom: 8px;
  border-bottom: 1px solid #e8e8e8;
}

.generated-title-row {
  display: flex;
  align-items: center;
  gap: 8px;
}

.generated-title {
  font-size: 14px;
  font-weight: 600;
  color: #111827;
  margin: 0;
}

.channel-pill {
  font-size: 11px;
  font-weight: 500;
  color: #6d28d9;
  background: #f3f1ff;
  padding: 4px 10px;
  border-radius: 999px;
}

.copy-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  background: #f3f1ff;
  border: 1px solid #e8e8e8;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 500;
  color: #6d28d9;
  cursor: pointer;
  transition: all 0.2s ease;
}

.copy-btn:hover {
  background: #ede7ff;
  border-color: #6d28d9;
}

.generated-content {
  background: #f9fafb;
  border: 1px solid #e8e8e8;
  border-radius: 8px;
  padding: 16px;
  max-height: 280px;
  overflow-y: auto;
}

.generated-content::-webkit-scrollbar {
  width: 3px;
}

.generated-content::-webkit-scrollbar-track {
  background: #f1f5f9;
  border-radius: 1px;
}

.generated-content::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 1px;
}

.generated-content::-webkit-scrollbar-thumb:hover {
  background: #94a3b8;
}

.content-text {
  font-size: 12px;
  color: #374151;
  line-height: 1.5;
  margin: 0;
  white-space: pre-wrap;
  font-family: inherit;
}
</style>
